// Chat module for ChatWidget - handles messages, suggested questions, notifications
import { createMessageHTML, scrollToBottom } from './ui.js';
import { saveMessages, saveShownSuggestions, saveReopenAfterRedirect } from './state.js';

// Add a message to the chat
export function addMessage(role, content, messages, messagesContainer, config) {
    const message = {
        role,
        content,
        timestamp: Date.now()
    };

    messages.push(message);
    saveMessages(messages);

    const welcome = messagesContainer.querySelector('.chat-widget-welcome');
    if (welcome) {
        welcome.remove();
    }

    const messageHTML = createMessageHTML(message, config);
    messagesContainer.insertAdjacentHTML('beforeend', messageHTML);
    scrollToBottom(messagesContainer);

    // Return the element that was just added (last child of messagesContainer)
    // This allows callers to scroll to this specific message
    const children = messagesContainer.children;
    return children[children.length - 1];
}

// Show suggested questions
export function showSuggestedQuestions(messagesContainer, suggestedQuestions, onQuestionClick) {
    // Remove existing suggestions container if present
    const existingContainer = messagesContainer.querySelector('#suggestedQuestionsContainer');
    if (existingContainer) {
        existingContainer.remove();
    }

    const container = document.createElement('div');
    container.className = 'suggested-questions-container';
    container.id = 'suggestedQuestionsContainer';

    let html = '<div class="suggested-questions-title">How can I help you today?</div>';
    html += '<div class="suggested-questions-list">';

    suggestedQuestions.forEach((sq, index) => {
        html += `<button class="suggested-question-btn" data-index="${index}">${sq.question}</button>`;
    });

    html += '</div>';
    container.innerHTML = html;

    messagesContainer.appendChild(container);
    scrollToBottom(messagesContainer);

    // Add click handlers
    container.querySelectorAll('.suggested-question-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const index = parseInt(btn.getAttribute('data-index'));
            const sq = suggestedQuestions[index];

            // Mark as shown
            saveShownSuggestions(true);

            // Call the callback with the question text and optional flow_id
            onQuestionClick(sq.question, sq.flow_id || 0);
        });
    });
}

// Hide suggested questions (but don't remove from DOM - just hide)
export function hideSuggestedQuestions(messagesContainer) {
    const container = messagesContainer.querySelector('#suggestedQuestionsContainer');
    if (container) {
        container.style.display = 'none';
    }
}

// Show suggested questions (if they exist in DOM)
export function showSuggestedQuestionsIfHidden(messagesContainer) {
    const container = messagesContainer.querySelector('#suggestedQuestionsContainer');
    if (container) {
        container.style.display = 'block';
        scrollToBottom(messagesContainer);
        return true;
    }
    return false;
}

// Add option buttons to a message (for multi-option rules)
export function addOptionButtonsToMessage(messageElement, options) {
    // Create a container for the option buttons
    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'option-buttons-container';

    // Create a button for each option
    options.forEach((option, index) => {
        const button = document.createElement('button');
        button.className = 'option-btn-compact';
        button.textContent = option.label;
        button.dataset.optionIndex = index + 1; // 1-indexed to match the marker format
        optionsContainer.appendChild(button);
    });

    // Find the message bubble within the message element
    const messageBubble = messageElement.querySelector('.chat-message-bubble');
    if (messageBubble) {
        // Insert the options container after the message bubble
        messageBubble.parentNode.insertBefore(optionsContainer, messageBubble.nextSibling);
    }
}

// Add choice buttons to a message (for state machine multiple choice steps)
// Supports both old format (array of strings) and new format (array of {key, label} objects)
// Callback receives (key, label) - key for sending to server, label for displaying to user
export function addChoiceButtonsToMessage(messageElement, choices, onChoiceClick) {
    // Create a container for the choice buttons
    const choicesContainer = document.createElement('div');
    choicesContainer.className = 'option-buttons-container';
    choicesContainer.dataset.choicesContainer = 'true'; // Mark for easy identification

    // Create a button for each choice
    choices.forEach((choice) => {
        const button = document.createElement('button');
        button.className = 'option-btn-compact';

        // Handle both old format (string) and new format ({key, label})
        const isNewFormat = typeof choice === 'object' && choice !== null && choice.key;
        const displayText = isNewFormat ? choice.label : choice;
        const valueToSend = isNewFormat ? choice.key : choice;

        // Display the label (translated text) but store the key (original) for sending
        button.textContent = displayText;
        button.dataset.choice = valueToSend; // Store original key for sending back to server

        // Add click handler
        button.addEventListener('click', () => {
            // Disable all choice buttons after one is clicked
            choicesContainer.querySelectorAll('button').forEach(btn => {
                btn.disabled = true;
            });

            // Call the callback with both key (for server) and label (for display)
            onChoiceClick(valueToSend, displayText);
        });

        choicesContainer.appendChild(button);
    });

    // Find the message bubble within the message element
    const messageBubble = messageElement.querySelector('.chat-message-bubble');
    if (messageBubble) {
        // Insert the choices container after the message bubble
        messageBubble.parentNode.insertBefore(choicesContainer, messageBubble.nextSibling);
    }
}

/**
 * Validate URL for safe redirection
 * SECURITY: Prevents javascript:, data:, and other unsafe URL schemes
 * @param {string} url - URL to validate
 * @returns {boolean} - True if URL is safe for redirection
 */
function isValidRedirectUrl(url) {
    if (!url || typeof url !== 'string') {
        return false;
    }

    // Trim whitespace
    url = url.trim();

    // Allow relative URLs (starting with / or ./)
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
        return true;
    }

    // For absolute URLs, only allow http: and https:
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch (e) {
        // Invalid URL format
        return false;
    }
}

// Show redirect confirmation
export function showRedirectConfirmation(messagesContainer, redirectUrl, onYes, onNo) {
    // SECURITY: Validate URL before displaying or redirecting
    if (!isValidRedirectUrl(redirectUrl)) {
        console.warn('Blocked unsafe redirect URL:', redirectUrl);
        // Show error message to user
        const errorDiv = document.createElement('div');
        errorDiv.className = 'message assistant';
        const errorContent = document.createElement('div');
        errorContent.className = 'message-content';
        errorContent.textContent = 'Sorry, the redirect URL is not valid.';
        errorDiv.appendChild(errorContent);
        messagesContainer.appendChild(errorDiv);
        scrollToBottom(messagesContainer);
        return;
    }

    // Create a special message with confirmation buttons
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message assistant';

    const contentDiv = document.createElement('div');
    contentDiv.className = 'message-content';

    // Create paragraph
    const para = document.createElement('p');
    para.style.marginBottom = '12px';
    para.textContent = 'Would you like me to take you to a page with more detailed information?';
    contentDiv.appendChild(para);

    // Create buttons container
    const buttonsDiv = document.createElement('div');
    buttonsDiv.style.display = 'flex';
    buttonsDiv.style.gap = '8px';

    // Create Yes button
    const yesBtn = document.createElement('button');
    yesBtn.className = 'redirect-btn redirect-yes';
    yesBtn.textContent = 'Yes, take me there';
    buttonsDiv.appendChild(yesBtn);

    // Create No button
    const noBtn = document.createElement('button');
    noBtn.className = 'redirect-btn redirect-no';
    noBtn.textContent = 'No, stay here';
    buttonsDiv.appendChild(noBtn);

    contentDiv.appendChild(buttonsDiv);
    messageDiv.appendChild(contentDiv);
    messagesContainer.appendChild(messageDiv);
    scrollToBottom(messagesContainer);

    // Add event listeners to the buttons
    yesBtn.addEventListener('click', () => {
        // Store flag to reopen chat after redirect
        saveReopenAfterRedirect(true);
        // Call the callback
        if (onYes) onYes(redirectUrl);
        // SECURITY: URL already validated above, safe to redirect
        window.location.href = redirectUrl;
    });

    noBtn.addEventListener('click', () => {
        // Remove the confirmation message
        messageDiv.remove();
        // Call the callback
        if (onNo) onNo();
    });
}
